import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth, apiErr } from "@/context/AuthContext";
import api from "@/lib/apiClient";
import GoogleButton from "@/components/GoogleButton";
import { GraduationCap, Loader2, Building2, User, MailCheck } from "lucide-react";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const [params] = useSearchParams();
  const [role, setRole] = useState(params.get("role") === "recruiter" ? "recruiter" : "student");
  const [form, setForm] = useState({ full_name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [resending, setResending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await register({ ...form, role });
      setSent(true);
    } catch (err) {
      setError(apiErr(err));
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    setResending(true);
    try {
      await api.post("/auth/resend-verification", { email: form.email });
      toast.success("Verification email re-sent.");
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setResending(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="min-h-screen bg-[#0F172A] flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 justify-center mb-8">
          <div className="h-10 w-10 rounded-lg bg-[#6366F1] flex items-center justify-center">
            <GraduationCap size={22} className="text-white" />
          </div>
          <span className="font-extrabold tracking-tight text-xl text-white">Enjyme</span>
        </Link>

        {sent ? (
          <div className="bg-white rounded-2xl p-8 border border-slate-200 shadow-xl text-center" data-testid="check-email-card">
            <div className="flex flex-col items-center gap-3">
              <MailCheck className="text-[#6366F1]" size={44} />
              <h1 className="font-display font-extrabold text-2xl tracking-tight text-slate-900">Check your email</h1>
              <p className="text-sm text-slate-500">We sent a verification link to <span className="font-semibold text-slate-700">{form.email}</span>. Click it to activate your account, then sign in.</p>
              <button type="button" data-testid="register-resend-btn" onClick={resend} disabled={resending}
                className="mt-1 text-sm font-semibold text-[#6366F1] hover:underline disabled:opacity-50">
                {resending ? "Sending…" : "Didn't get it? Resend email"}
              </button>
              <Link to="/login" data-testid="check-email-to-login" className="mt-3 w-full bg-[#6366F1] hover:bg-[#4F46E5] active:scale-[0.98] text-white font-semibold py-2.5 rounded-lg transition-colors">
                Go to sign in
              </Link>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-2xl p-8 border border-slate-200 shadow-xl">
            <h1 className="font-display font-extrabold text-2xl tracking-tight text-slate-900">Create your account</h1>
            <p className="text-sm text-slate-500 mt-1 mb-6">Start operating your Career GPS.</p>

            <GoogleButton />

            <div className="flex items-center gap-3 my-5">
              <div className="h-px flex-1 bg-slate-200" />
              <span className="text-xs text-slate-400">or sign up with email</span>
              <div className="h-px flex-1 bg-slate-200" />
            </div>

            <div className="grid grid-cols-2 gap-3 mb-6">
              {[{ id: "student", label: "Student", icon: User }, { id: "recruiter", label: "Recruiter", icon: Building2 }].map((r) => (
                <button key={r.id} type="button" data-testid={`role-${r.id}`} onClick={() => setRole(r.id)}
                  className={`flex items-center gap-2 justify-center py-3 rounded-lg border-2 text-sm font-semibold transition-colors ${
                    role === r.id ? "border-[#6366F1] bg-[#6366F1]/5 text-[#4F46E5]" : "border-slate-200 text-slate-500 hover:border-slate-300"
                  }`}>
                  <r.icon size={16} /> {r.label}
                </button>
              ))}
            </div>

            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-700">Full name</label>
                <input data-testid="register-name" required value={form.full_name} onChange={set("full_name")}
                  className="mt-1 w-full px-4 py-2.5 rounded-lg border border-slate-300 text-slate-900 focus:border-[#6366F1] focus:ring-2 focus:ring-[#6366F1]/20 outline-none transition-colors" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Email</label>
                <input data-testid="register-email" type="email" required value={form.email} onChange={set("email")}
                  className="mt-1 w-full px-4 py-2.5 rounded-lg border border-slate-300 text-slate-900 focus:border-[#6366F1] focus:ring-2 focus:ring-[#6366F1]/20 outline-none transition-colors" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Password</label>
                <input data-testid="register-password" type="password" required minLength={6} value={form.password} onChange={set("password")}
                  className="mt-1 w-full px-4 py-2.5 rounded-lg border border-slate-300 text-slate-900 focus:border-[#6366F1] focus:ring-2 focus:ring-[#6366F1]/20 outline-none transition-colors" />
              </div>
              {error && <p data-testid="register-error" className="text-sm text-red-600">{error}</p>}
              <button data-testid="register-submit" disabled={loading}
                className="w-full flex items-center justify-center gap-2 bg-[#6366F1] hover:bg-[#4F46E5] active:scale-[0.98] text-white font-semibold py-2.5 rounded-lg transition-colors disabled:opacity-60">
                {loading && <Loader2 size={16} className="animate-spin" />} Create account
              </button>
            </form>
            <p className="text-sm text-slate-500 mt-6 text-center">
              Already have an account? <Link to="/login" className="text-[#6366F1] font-semibold hover:underline">Sign in</Link>
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
